<script lang="ts">
	import Button from '$lib/buttons/v3/button.svelte';
	import Close from '$lib/icons/close.svelte';
	import { trackEvent } from '$lib/segment/client';
	import { type Snippet } from 'svelte';

	type ModalSize = 'regular' | 'large' | 'extra-large';

	interface Props {
		size?: ModalSize;
		withDesktopPadding?: boolean;
		onclose?: () => void;
		onhide?: (payload?: Record<string, unknown>) => void;
		children: Snippet;
		class?: string;
		modalName?: string;
	}

	let {
		size = 'regular',
		withDesktopPadding = true,
		onclose,
		onhide,
		children,
		class: className,
		modalName
	}: Props = $props();

	let modal: HTMLDialogElement;
	let closing = $state(false);

	export function show() {
		document.body.appendChild(modal);
		modal.showModal();

		if (modalName) {
			trackEvent(`Show ${modalName}`);
		}
	}

	let closeTimeout: NodeJS.Timeout;
	export function hide(payload?: Record<string, unknown>) {
		closing = true;
		closeTimeout = setTimeout(() => {
			modal.close();
			modal?.remove();
		}, 500);

		onhide?.(payload);

		if (modalName) {
			trackEvent(`Hide ${modalName}`, payload);
		}
	}

	export function remove() {
		clearTimeout(closeTimeout);

		if (!modal || !document.body.contains(modal)) return;

		modal.remove();
	}

	$effect(() => {
		if (modal) {
			document.body.appendChild(modal);
		}

		return () => {
			remove();
		};
	});

	const sizeClasses: Record<ModalSize, string> = {
		regular: 'w-full xl:w-2/5 xl:max-w-[43.5rem]',
		large: 'w-full xl:w-3/5 lg:max-w-[696px]',
		'extra-large': 'h-full w-full lg:aspect-3/2 lg:h-full lg:w-5/6 xl:w-2/3 3xl:w-1/2'
	};

	let sizeClass = $derived(sizeClasses[size]);
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<dialog
	data-testid="modal-dialog"
	bind:this={modal}
	onclose={() => {
		closing = false;
		onclose?.();
	}}
	class="z-50 m-0 w-full overflow-visible bg-transparent p-0 antialiased outline-none max-lg:h-full max-lg:max-h-full max-lg:max-w-full lg:m-auto"
	class:dialog-in={!closing}
	class:dialog-out={closing}
	onclick={(e) => {
		if (e.target === modal) {
			hide?.();
		}
	}}
	onkeydown={(e) => {
		if (e.key === 'Escape') {
			e.preventDefault();
			hide?.();
			return;
		}
	}}
>
	<div
		class="scrollbar-hide max-h-screen overflow-y-auto max-lg:absolute max-lg:inset-x-0 max-lg:bottom-0 lg:pointer-events-none"
	>
		<div
			class={[
				'modal-content relative rounded-t-[20px] bg-white px-6 py-8 text-brown-2 sm:px-12 sm:py-16 lg:pointer-events-auto lg:mx-auto lg:rounded-[20px]',
				className,
				sizeClass,
				{
					'sm-py-16 sm:px-12': withDesktopPadding,
					'sm:px-0 sm:py-0': !withDesktopPadding
				}
			]}
		>
			{@render children()}
			<div class="fixed right-4 top-4 lg:absolute" data-testid="modal-close-wrapper">
				<Button class="btn-white" onclick={() => hide()} title="Close modal"><Close /></Button>
			</div>
		</div>
	</div>
</dialog>

<style lang="scss">
	@keyframes dialogIn {
		from {
			transform: translate3d(0, 100vh, 0);
		}
		to {
			transform: translate3d(0, 0, 0);
		}
	}

	@keyframes dialogOut {
		from {
			transform: translate3d(0, 0, 0);
		}
		to {
			transform: translate3d(0, 100vh, 0);
		}
	}

	@keyframes fadeIn {
		from {
			opacity: 0.01;
		}
		to {
			opacity: 1;
		}
	}

	@keyframes fadeOut {
		from {
			opacity: 1;
		}
		to {
			opacity: 0;
		}
	}

	dialog {
		& div.modal-content {
			animation-fill-mode: forwards;

			@media (min-width: 1280px) {
				box-shadow: 0px 16px 32px rgba(0, 0, 0, 0.16);
			}
		}

		&.dialog-in {
			& > div {
				transform: translate3d(0, 0, 0);
				animation: dialogIn 0.5s ease-in-out;
			}

			&::backdrop {
				opacity: 1;
				animation: fadeIn 0.5s ease-in-out;
			}
		}

		&.dialog-out {
			& > div {
				transform: translate3d(0, 100vh, 0);
				animation: dialogOut 0.5s ease-in-out;
			}

			&::backdrop {
				opacity: 0;
				animation: fadeOut 0.5s ease-in-out;
			}
		}

		&::backdrop {
			background-color: theme('colors.black/48%');
		}
	}
</style>
